import { CustomColorThemesContext } from "../../contexts/CustomColorThemesContext";
import FrameContext from "../../core/components/PagePreview/Context";
import { DEFAULT_COLORWAY } from "../../themes/constants";
import { selectSite } from "../selectors";
import FancyClothesContext from "./FancyClothesContext";
import { applyCustomColorOverrides } from "./applyCustomColorOverrides";
import { convertDekuraToFancyClothes } from "./convertDekuraToFancyClothes";
import { DesignProvider } from "@minted/fancyclothes";
import merge from "lodash/merge";
import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

const selectDesignData = ({ resources, theme }) => {
  const { mintedProductColorChoiceId, themeDataOverrides, themeId } =
    selectSite(resources.sites);

  return {
    colorway: themeId
      ? DEFAULT_COLORWAY
      : theme.colorwayCodeById[mintedProductColorChoiceId],
    fancyClothesData: theme.fancyClothesData,
    themeDataOverrides,
  };
};

const FancyClothesProvider = ({ children }) => {
  const {
    colorway,
    // The fancyclothes data includes scopes for all colorways, making it more
    // difficult to parse through. Themebuilder may use the "full" fancyclothes
    // data, but it may be good for hitchedup to receive/use a more directed
    // structure in the future
    fancyClothesData: originalFancyClothesData,
    themeDataOverrides,
  } = useSelector(selectDesignData);
  const siteColorThemeId = useSelector((state) => {
    const site = selectSite(state.resources.sites);

    return site.customColorTheme;
  });

  const { customColorThemes } = useContext(CustomColorThemesContext);

  const typographyOverrides = useMemo(
    () => convertDekuraToFancyClothes(themeDataOverrides || {}),
    [themeDataOverrides],
  );

  const fancyClothesData = useMemo(() => {
    const mergedDesign = merge(
      {},
      originalFancyClothesData,
      typographyOverrides,
    );

    if (siteColorThemeId) {
      const colorTheme = customColorThemes.find(
        (theme) => theme.id === siteColorThemeId,
      );

      if (colorTheme) {
        return {
          colorway,
          design: applyCustomColorOverrides(mergedDesign, colorway, colorTheme),
        };
      }
    }

    return {
      colorway,
      design: mergedDesign,
    };
  }, [
    originalFancyClothesData,
    typographyOverrides,
    siteColorThemeId,
    colorway,
    customColorThemes,
  ]);

  return (
    <FancyClothesContext.Provider value={fancyClothesData}>
      <FrameContext.Consumer>
        {({ document }) => (
          <DesignProvider
            colorway={colorway}
            design={fancyClothesData.design}
            styleContainer={document ? document.head : undefined}
          >
            {children}
          </DesignProvider>
        )}
      </FrameContext.Consumer>
    </FancyClothesContext.Provider>
  );
};

FancyClothesProvider.displayName = "FancyClothesProvider";

export default FancyClothesProvider;
